/**
 * Timestamps shown on the site, always labeled with their time zone so the
 * reader never has to guess which one it is.
 *
 * Admin pages are in Japan time. A user's own pages are in Japan time for
 * calls to Japan, and in the viewer's own zone otherwise.
 */

export const JST = 'Asia/Tokyo';

/** The zone to show a call's times in: Japan time for Japan, else the viewer's own (undefined). */
export function zoneForCountry(country: string | null | undefined): string | undefined {
  return country === 'JP' ? JST : undefined;
}

const DATE_TIME: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

const TIME: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', second: '2-digit' };

function format(iso: string | null | undefined, options: Intl.DateTimeFormatOptions, timeZone?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  // English locales name Tokyo "GMT+9", so JST is spelled out.
  if (timeZone === JST) return `${d.toLocaleString(undefined, { ...options, timeZone })} JST`;
  return d.toLocaleString(undefined, { ...options, timeZone, timeZoneName: 'short' });
}

/** Date and time, e.g. "Oct 26, 2026, 5:03 PM JST". */
export function fmtDateTime(iso: string | null | undefined, timeZone?: string): string {
  return format(iso, DATE_TIME, timeZone);
}

/** Time of day with seconds, e.g. "5:03:24 PM JST". */
export function fmtTime(iso: string | null | undefined, timeZone?: string): string {
  return format(iso, TIME, timeZone);
}

/** Admin pages: Japan time, to the second, e.g. "Oct 26, 2026, 5:03:24 PM JST". */
export function fmtAdminDateTime(iso: string | null | undefined): string {
  return format(iso, { ...DATE_TIME, second: '2-digit' }, JST);
}
